
<script lang="ts">
    import { fade } from 'svelte/transition';

    let difficulty = 0;
    let hits: number[] = [];
    let hitsStrs: string[] = [];
    let count = 1;
    let log = [];
    $: current = generate(0);

    let debug = true;

    let gift: string | undefined = undefined;
    let gifts: string[] = [];

    function setDifficulty(dfc: number) {
        difficulty = dfc;
        current = generate(difficulty);
    }

    function generate(difficulty) {
        if (difficulty === 0) {
            return {}
        }

        let addRange: number
        let subRange: number
        let mulRange: number
        let divRange: number
        let budget: number
        let ops: string[]

        let opsPrice = {
            "+": 3,
            "-": 5,
            "*": 20,
            "/": 30,
        }

        switch(difficulty) {
            case 1:
                addRange = 9;
                subRange = 0;
                mulRange = 0;
                divRange = 0;
                budget = 9;
                ops = ["+"];
                break;
            case 2:
                addRange = 16;
                subRange = 0;
                mulRange = 0;
                divRange = 0;
                budget = 10;
                ops = ["+"];
                break;
            case 3:
                addRange = 25;
                subRange = 0;
                mulRange = 0;
                divRange = 0;
                budget = 11;
                ops = ["+"];
                break;
            case 4:
                addRange = 50;
                subRange = 0;
                mulRange = 0;
                divRange = 0;
                budget = 10;
                ops = ["+"];
                break;
            case 5:
                addRange = 9;
                subRange = 5;
                mulRange = 0;
                divRange = 0;
                budget = 10;
                ops = ["+", "-", "-", "-"];
                break;
        }

        const randomGauss = () => {
            const theta = 2 * Math.PI * Math.random();
            const rho = Math.sqrt(-2 * Math.log(1 - Math.random()));
            return (rho * Math.cos(theta)) / 10.0 + 0.5;
        };

        function range(value: number, budget: number) {
            switch (budget) {
                case 0:
                    return 1;
                case 1:
                    return value / 2;
                case 2:
                    return value;
                case 3:
                    return value * 1.5;
                case 4:
                    return value * 2;
                default:
                    return value;
            }
        }

        function addNumber(budget: number) {
            return Math.floor(Math.random() * range(addRange, budget));
        }
        function subNumber(budget: number) {
            return Math.floor(Math.random() * range(subRange, budget));
        }
        function mulNumber(budget: number) {
            return Math.floor(Math.random() * range(mulRange, budget));
        }
        function divNumber(budget: number) {
            return Math.floor(Math.random() * range(divRange, budget));
        }

        function subexpression(budget: number, parentOp?: string) {
            if (budget <= 3) {
                switch (parentOp) {
                    case "+":
                        return addNumber(budget);
                    case "-":
                        return subNumber(budget);
                    case "*":
                        return mulNumber(budget);
                    case "/":
                        return divNumber(budget);
                    case undefined:
                        return addNumber(budget);
                }
            }

            var possibleOps = ops.filter(op => opsPrice[op] < budget);
            possibleOps.push("+");

            let op = possibleOps[Math.floor(Math.random() * possibleOps.length)];
            let opPrice = opsPrice[op];

            let opBudget = budget - opPrice;
            let leftBudget = Math.floor(opBudget * randomGauss());
            let rightBudget = opBudget - leftBudget;
            let left = subexpression(Math.max(1, leftBudget), op);
            let right = subexpression(Math.max(1, rightBudget), op);

            if (debug) {
                console.log(`(${budget}<${leftBudget},${rightBudget}>: ${left} ${op} ${right})`);
            }
            return `${left} ${op} ${right}`;
        }

        //let budgetRandomized = budget + Math.floor(Math.random() * 4) - 2;
        let problem = subexpression(budget, undefined);
        let solution = eval(problem);

        let options = [solution];
        for (let i = 0; i < 3; i++) {
            let option = solution + Math.floor(Math.random() * 10) - 5;
            while (options.includes(option)) {
                option = solution + Math.floor(Math.random() * 10) - 5;
            }
            options.push(option);
        }
        // Sort numerically
        options.sort((a, b) => a - b);
        options.push('🙈');

        return {
            problem: problem,
            solution: solution,
            options: options
        }
    }

    let possibleGifts = [
        '🧚‍♀️', '🐶',
        '🐱', '🐭',
        '💎', '🎉',
        '🐣', '🎈',
        '🎊', '🎀',
        '🏆', '🥇',
        '🍭', '🍬',
        '🍫', '🍪',
        '🍩', '🍰',
        '🐇', '🦜',
        '🦢', '🦚',
        '🦋', '🐞',
    ]

    function guess(problem: string, option: string, solution: string, difficulty: number) {
        let newHits = hits;
        if (option == '🙈') {
            newHits.push(0);
        } else if (option == solution) {
            newHits.push(difficulty);

            // Generate gift if needed
            let goodHits = newHits.filter(hit => hit > 0);
            if (goodHits.length % 10 == 0) {
                if (gifts.length == 0) {
                    gift = '🎁';
                } else {
                    gift = possibleGifts[Math.floor(Math.random() * possibleGifts.length)];
                }
            }
        } else {
            newHits.push(-difficulty);
        }
        hits = newHits;
        hitsStrs = showHits(hits)
        count += 1;
        current = generate(difficulty);

        // Prepend the log
        log.unshift({
            count: count - 1,
            problem: problem,
            option: option,
            solution: solution,
            hitStr: hitsStrs[hitsStrs.length - 1],
            gift: gift,
        });
        log = log;
    }

    function showHits(hits: number[]) {
        let result = [];
        let emojis = [
            '🙈',
            "🙂",
            "😊",
            "🤩",
            "😂",
            "🥳",
            "😻",
            "👩‍🎓",
            "🐴",
            "🦄",
        ];
        hits.forEach(element => {
            if (element >= 0) {
                result.push(emojis[element]);
            } else {
                result.push("😶‍🌫️");
            }
        });

        return result;
    }

    function computeScore(hits: number[]) {
        let _hits = 0;
        let _misses = 0;
        hits.filter(hit => hit > 0).forEach(hit => {
            _hits += 1;
        });
        return `${_hits} / ${hits.length}`;
    }

    $: reveal = () => {
        let newGifts = gifts;
        newGifts.push(gift);
        gifts = newGifts;

        gift = undefined;
    }
</script>

<main>
    <h2>Numerická fontána ⛲️🦄</h2>
    Obtížnost<br/>
    { #each Array(5) as _, i }
        <button
            on:click={() => setDifficulty(i + 1)}
            class:active={difficulty === i + 1}
        >
            { i + 1 }
        </button>
    { /each }

    <div transition:fade={{ delay: 250, duration: 300 }}>
        { #if (difficulty > 0) }
            <br/>
            <h2>Skóre { computeScore(hits) }</h2>
                { #each hitsStrs as hit, i }
                    <span
                        transition:fade={{ delay: 100, duration: 500 }}
                    >{ hit }</span>
                    { #if (i % 10 == 9) }
                        <br/>
                    { /if }
                { /each }
            <h2>Poklady</h2>
                { #each gifts as gift }
                    <span transition:fade={{ delay: 100, duration: 500 }}>{ gift }</span>
                { /each }
            <br/>

            { #if (gift !== undefined) }
                <span on:click={ () => reveal() } class='gift'>🎁</span>
            { :else }
                <h2>Příklad číslo { count }:</h2>
                <h3>{ current.problem } = </h3>

                { #each current.options as option }
                    <button on:click={ () => guess(current.problem, option, current.solution, difficulty) }>
                        { option }
                    </button>
                { /each }
            { /if }

            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            <h3>Výsledky</h3>
            <table class='center'>
            { #each log as logItem, i }
                { #if i == 0 && gift !== undefined }
                    <!-- hide till gift unwrapped -->
                { :else }
                    <tr>
                        <td style="text-align: left; min-width:4em;">
                            { logItem.count }.
                        </td>
                        <td style="text-align: right">
                            { logItem.problem }
                        </td>
                        <td>
                            =
                        </td>
                        <td>
                            { #if (logItem.option != '🙈') }
                                { logItem.option }
                            { /if }
                        </td>
                        <td>
                            { #if (logItem.option != logItem.solution) }
                                (správně: { logItem.solution })
                            { /if }
                        </td>
                        <td>
                            { logItem.hitStr }
                        </td>
                        <td>
                            { #if (logItem.gift !== undefined) }
                                { logItem.gift }
                            { /if }
                        </td>
                    </tr>
                { /if }
            { /each }
            </table>
        { /if }
    </div>


    <br/>
    <br/>
    <br/>
</main>

<style>
    :global(body) {
        background-color: #fef9ff;
    }

	main {
		text-align: center;
		padding: 1em;
		max-width: 280px;
		margin: 0 auto;
	}

	@media (min-width: 640px) {
		main {
			max-width: none;
		}
	}

    button {
        background-color: #f4f4f4;
        border: 1px solid #ccc;
        padding: 0.5em 1em;
        margin: 0.1em;
        cursor: pointer;
    }

    button.active {
        background-color: #333;
        color: white;
    }

    span.gift {
        font-size: 10em;
        cursor: pointer;
    }

    .center {
        margin-left: auto;
        margin-right: auto;
    }

    tr:nth-child(odd) {
        background: #f2fcff
    }
</style>
